import { BaseApi } from '../baseApi';
import type { APIResponse } from '../config';
import type { User, UserRole } from '@/types/models';

// Define the base types for login credentials
export type PasswordLoginCredentials = {
    email: string;
    password: string;
};

export type SSOLoginCredentials = {
    sso_token: string;
    district_domain: string;
};

export type LoginCredentials = PasswordLoginCredentials | SSOLoginCredentials;

export interface SSOConfig {
    provider: 'google' | 'azure';
    client_id: string;
    tenant_id?: string;  // Made optional instead of nullable
    metadata_url: string;
}

export interface LoginError extends Error {
    sso_config?: SSOConfig;
    details?: string;
}

export interface RegisterData {
    email: string;
    password: string;
    first_name: string;
    last_name: string;
    district_domain: string;
    role: UserRole;
    school_code?: string | null;
}

export interface AuthResponse {
    token: string;
    user: User;
}

export interface LoginResponse {
    token: string;
    user: {
        id: string;
        email: string;
        role: string;
        first_name: string;
        last_name: string;
        district_id: number;
        school_id: number;
    };
}

export interface TenantConfig {
    tenant_id: number;
    name: string;
    domain: string;
    auth: {
        sso_enabled: boolean;
        provider: 'google' | 'azure' | null;
        client_id: string | null;
        tenant_id: string | null;
        metadata_url: string | null;
        allow_password_login: boolean;
    };
}

export class AuthApi extends BaseApi {
    async getTenantConfig(): Promise<APIResponse<TenantConfig>> {
        try {
            const response = await this.makeGet<APIResponse<TenantConfig>>('/auth/tenant-config');
            return response;
        } catch (error) {
            console.error('Get tenant config error:', error);
            throw error;
        }
    }

    async login(credentials: LoginCredentials): Promise<APIResponse<LoginResponse>> {
        try {
            console.log('Making login request with credentials:', 
                'email' in credentials ? { email: credentials.email } : { sso: true }
            );
            const response = await this.makePost<APIResponse<LoginResponse>>('/auth/login', credentials);
            console.log('Raw login response:', response);
            
            if (!response?.data?.token || !response?.data?.user) {
                console.error('Invalid response structure:', response);
                throw new Error('Invalid response format from server');
            }
            
            return response;
        } catch (error) {
            console.error('Login request error:', error);
            throw error;
        }
    }

    async register(data: RegisterData): Promise<APIResponse<LoginResponse>> {
        try {
            const response = await this.makePost<APIResponse<LoginResponse>>('/auth/register', data);
            console.log('Raw register response:', response);
            
            if (!response?.data?.token || !response?.data?.user) {
                console.error('Invalid response structure:', response);
                throw new Error('Invalid response format from server');
            }
            
            return response;
        } catch (error) {
            console.error('Registration error:', error);
            throw error;
        }
    }

    async logout(): Promise<void> {
        try {
            return await this.makePost('/auth/logout', {});
        } catch (error) {
            console.error('Logout error:', error);
            throw error;
        }
    }
} 